import { useMemo, useState } from 'react';
import {
  Building2, Check, ChevronDown, CircleUser, Lock, Plus, RotateCcw, Search, ShieldCheck, X,
} from 'lucide-react';
import {
  ALL_PERMISSIONS, PERMISSION_META, STAFF_ROLES, hasPermission,
  type Permission, type Role,
} from '@pointage/shared';
import { toast } from '../../store/toast.js';
import {
  useEntities, useInviteUser, useMe, useUpdateUser, useUsers,
  type EntityDTO, type UpdateUserInput, type UserDTO,
} from '../../api/hooks.js';

// Accès & utilisateurs — the whole access-control cockpit:
//   invite by email · role per account · per-permission overrides ·
//   entity scope. Server rails mirrored here: self row locked, last
//   BOSS locked, EMPLOYEE accounts activation-only.

export const ROLE_LABEL: Record<Role, string> = {
  BOSS: 'Boss', ADMIN: 'Administrateur', MANAGER: 'Manager', ASSISTANT: 'Assistante', EMPLOYEE: 'Employé',
  RECRUTEUR: 'Manager recruteur',
};
const ROLE_CLS: Record<Role, string> = {
  BOSS: 'boss', ADMIN: 'admin', MANAGER: 'manager', ASSISTANT: 'assistant', EMPLOYEE: 'employee',
  RECRUTEUR: 'manager',
};
const AVATAR_COLORS = ['var(--shf)', 'var(--sgn)', 'var(--oct)', 'var(--sga)'];
const initials = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';

const GROUP_ORDER = ['Pointage', 'Données', 'Paie', 'Talent', 'Bureau', 'Système'];

interface Props { ping: () => void; }

export function AccessCard({ ping }: Props) {
  const me = useMe();
  const users = useUsers();
  const entities = useEntities();
  const invite = useInviteUser();
  const update = useUpdateUser();

  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [showEmployees, setShowEmployees] = useState(false);
  const [invEmail, setInvEmail] = useState('');
  const [invRole, setInvRole] = useState<Role>('MANAGER');

  const canManage = (me.data?.permissions ?? []).includes('users.manage');
  const myId = me.data?.user.id;
  const rows = users.data ?? [];
  const ents = entities.data ?? [];

  // Les managers recruteurs se gèrent dans leur rubrique (Managers recruteurs).
  const staff = rows.filter((u) => u.role !== 'EMPLOYEE' && u.role !== 'RECRUTEUR');
  const activeBosses = staff.filter((u) => u.role === 'BOSS' && u.active);
  const employees = rows.filter((u) => u.role === 'EMPLOYEE');
  const q = query.trim().toLowerCase();
  const filtered = q
    ? staff.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    : staff;

  const patch = (u: UserDTO, body: Omit<UpdateUserInput, 'id'>) => {
    update.mutate({ id: u.id, ...body }, {
      onSuccess: ping,
      onError: (e) => {
        const code = (e as Error & { body?: { error?: string } }).body?.error;
        toast.danger(
          code === 'last_boss' ? 'Impossible : dernier compte Boss actif.'
          : code === 'cannot_edit_self' ? 'Vous ne pouvez pas modifier votre propre accès.'
          : 'Échec de l’enregistrement.',
        );
      },
    });
  };

  const doInvite = () => {
    const email = invEmail.trim().toLowerCase();
    if (!email) return;
    invite.mutate({ email, role: invRole }, {
      onSuccess: (u) => { setInvEmail(''); setOpen(u.id); ping(); },
      onError: (e) => {
        const code = (e as Error & { body?: { error?: string } }).body?.error;
        toast.danger(code === 'user_exists' ? 'Ce compte existe déjà.' : 'Invitation impossible — vérifiez l’email.');
      },
    });
  };

  return (
    <div className="scard-body">
      {canManage && (
        <div className="addbar acc-invite" data-testid="acc-invite-bar">
          <span className="addbar-label">Inviter un utilisateur — <b>l’accès est actif dès sa première connexion Google</b></span>
          <input className="finput" type="email" placeholder="email@shiftin.co" value={invEmail}
            onChange={(e) => setInvEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doInvite(); }}
            data-testid="acc-invite-email" aria-label="Email à inviter" />
          <div className="seg" role="radiogroup" aria-label="Rôle du nouvel utilisateur">
            {STAFF_ROLES.map((r) => (
              <button key={r} className={'seg-btn' + (invRole === r ? ' sel' : '')}
                onClick={() => setInvRole(r)} data-testid={`acc-invite-role-${r}`}>
                {ROLE_LABEL[r]}
              </button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={doInvite}
            disabled={invite.isPending || !invEmail.trim()} data-testid="acc-invite-submit">
            <Plus /> Inviter
          </button>
        </div>
      )}

      {staff.length > 6 && (
        <div className="acc-search">
          <Search aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filtrer par nom ou email…"
            aria-label="Filtrer les utilisateurs" data-testid="acc-search" />
        </div>
      )}

      <div className="user-list">
        {filtered.map((u, i) => (
          <UserRow key={u.id} u={u} idx={i} ents={ents}
            isSelf={u.id === myId}
            isLastBoss={u.role === 'BOSS' && u.active && activeBosses.length <= 1}
            canManage={canManage}
            open={open === u.id}
            onToggle={() => setOpen(open === u.id ? null : u.id)}
            onPatch={(body) => patch(u, body)} />
        ))}
        {filtered.length === 0 && <div className="srow"><div className="srow-help">Aucun utilisateur ne correspond.</div></div>}
      </div>

      {employees.length > 0 && (
        <div className="acc-empl">
          <button className="acc-empl-head" onClick={() => setShowEmployees(!showEmployees)} data-testid="acc-empl-toggle">
            <CircleUser aria-hidden="true" />
            <span>Comptes Espace employé</span>
            <span className="acc-empl-count">{employees.length}</span>
            <ChevronDown className={'chev' + (showEmployees ? ' up' : '')} aria-hidden="true" />
          </button>
          {showEmployees && (
            <div className="user-list" data-testid="acc-empl-list">
              {employees.map((u, i) => (
                <div key={u.id} className="urow compact" data-testid={`set-user-${u.id}`}>
                  <div className="u-av" style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                    {initials(u.name)}<span className={'pres ' + (u.lastLoginAt ? 'online' : 'never')} />
                  </div>
                  <div>
                    <div className="u-name">{u.name}</div>
                    <div className="u-mail">{u.email}</div>
                  </div>
                  <span className="role-badge employee">Espace employé</span>
                  {canManage && (
                    <button className={'toggle sm' + (u.active ? ' on' : '')} role="switch" aria-checked={u.active}
                      aria-label={`Compte actif — ${u.name}`} data-testid={`acc-active-${u.id}`}
                      onClick={() => patch(u, { active: !u.active })} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── one staff row + its expandable access panel ────────────────
function UserRow({ u, idx, ents, isSelf, isLastBoss, canManage, open, onToggle, onPatch }: {
  u: UserDTO; idx: number; ents: EntityDTO[];
  isSelf: boolean; isLastBoss: boolean; canManage: boolean;
  open: boolean; onToggle: () => void;
  onPatch: (body: { role?: Role; active?: boolean; permsGranted?: string[]; permsRevoked?: string[]; entityScope?: string[] }) => void;
}) {
  const roleLocked = !canManage || isSelf || isLastBoss;
  const overrides = u.permsGranted.length + u.permsRevoked.length;

  return (
    <div className={'urow-wrap' + (open ? ' open' : '')}>
      <button type="button" className="urow expand" onClick={onToggle}
        aria-expanded={open} data-testid={`set-user-${u.id}`}>
        <div className="u-av" style={{ background: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}>
          {initials(u.name)}<span className={'pres ' + (u.lastLoginAt ? 'online' : 'never')} />
        </div>
        <div className="u-id">
          <div className="u-name">
            {u.name}
            {isSelf && <span className="u-you">vous</span>}
            {!u.active && <span className="u-off">désactivé</span>}
          </div>
          <div className="u-mail">{u.email}</div>
        </div>
        <div className="u-meta">
          {u.lastLoginAt
            ? <>Dernière connexion<br /><span className="when">{u.lastLoginAt.replace('T', ' ').slice(0, 16)}</span></>
            : <span className="never">Jamais connecté — invité</span>}
        </div>
        {u.entityScope.length > 0 && (
          <span className="scope-mini" title="Accès limité par entité">
            <Building2 aria-hidden="true" />{u.entityScope.length}
          </span>
        )}
        {overrides > 0 && (
          <span className="ovr-mini" title="Permissions personnalisées">
            <ShieldCheck aria-hidden="true" />{overrides}
          </span>
        )}
        <span className={`role-badge ${ROLE_CLS[u.role]}`}>{ROLE_LABEL[u.role]}</span>
        <ChevronDown className={'chev' + (open ? ' up' : '')} aria-hidden="true" />
      </button>

      {open && (
        <div className="u-panel" data-testid={`acc-panel-${u.id}`}>
          <div className="u-panel-row">
            <div className="u-panel-label">Rôle</div>
            <div className="seg" role="radiogroup" aria-label={`Rôle de ${u.name}`}>
              {STAFF_ROLES.map((r) => (
                <button key={r} className={'seg-btn' + (u.role === r ? ' sel' : '')}
                  disabled={roleLocked} data-testid={`acc-role-${u.id}-${r}`}
                  title={isLastBoss && u.role === 'BOSS' ? 'Dernier Boss actif — intouchable' : undefined}
                  onClick={() => r !== u.role && onPatch({ role: r })}>
                  {ROLE_LABEL[r]}
                </button>
              ))}
            </div>
            <div className="u-panel-spacer" />
            <div className="u-panel-label">Compte actif</div>
            <button className={'toggle' + (u.active ? ' on' : '')} role="switch" aria-checked={u.active}
              disabled={roleLocked} data-testid={`acc-active-${u.id}`}
              aria-label={`Compte actif — ${u.name}`}
              onClick={() => onPatch({ active: !u.active })} />
          </div>

          <div className="u-panel-row">
            <div className="u-panel-label">Entités visibles</div>
            <div className="scope-chips" data-testid={`acc-scope-${u.id}`}>
              <button
                className={'scope-chip' + (u.entityScope.length === 0 ? ' sel' : '')}
                disabled={!canManage}
                onClick={() => u.entityScope.length > 0 && onPatch({ entityScope: [] })}>
                <Check aria-hidden="true" /> Toutes
              </button>
              {ents.map((e) => {
                const selAll = u.entityScope.length === 0;
                const sel = u.entityScope.includes(e.id);
                return (
                  <button key={e.id}
                    className={'scope-chip ent' + (sel ? ' sel' : '') + (selAll ? ' dim' : '')}
                    style={{ '--ec': e.color } as React.CSSProperties}
                    disabled={!canManage} data-testid={`acc-scope-${u.id}-${e.id}`}
                    onClick={() => {
                      const next = sel ? u.entityScope.filter((x) => x !== e.id) : [...u.entityScope, e.id];
                      onPatch({ entityScope: next });
                    }}>
                    <span className="edot" />{e.short}
                  </button>
                );
              })}
            </div>
          </div>
          {u.entityScope.length > 0 && (
            <p className="u-panel-hint">
              Ne voit et n’agit que sur {u.entityScope.length} entité{u.entityScope.length > 1 ? 's' : ''} —
              rosters, congés, contrats, évaluations et rapports inclus.
            </p>
          )}

          <PermMatrix u={u} canManage={canManage} onPatch={onPatch} />
        </div>
      )}
    </div>
  );
}

// ─── permission matrix (role default ± overrides) ───────────────
function PermMatrix({ u, canManage, onPatch }: {
  u: UserDTO; canManage: boolean;
  onPatch: (body: { permsGranted?: string[]; permsRevoked?: string[] }) => void;
}) {
  const groups = useMemo(() => {
    const map = new Map<string, Permission[]>();
    for (const p of ALL_PERMISSIONS) {
      const g = PERMISSION_META[p].group;
      map.set(g, [...(map.get(g) ?? []), p]);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ group: g, perms: map.get(g)! }));
  }, []);

  const isBoss = u.role === 'BOSS';
  const locked = !canManage || isBoss;
  const effective = new Set(u.permissions);

  const toggle = (p: Permission) => {
    const base = hasPermission(u.role, p);
    const on = effective.has(p);
    const granted = new Set(u.permsGranted);
    const revoked = new Set(u.permsRevoked);
    if (on) { base ? revoked.add(p) : granted.delete(p); }
    else { base ? revoked.delete(p) : granted.add(p); }
    onPatch({ permsGranted: [...granted], permsRevoked: [...revoked] });
  };

  const reset = () => onPatch({ permsGranted: [], permsRevoked: [] });
  const overridden = u.permsGranted.length + u.permsRevoked.length > 0;

  return (
    <div className="pmatrix" data-testid={`acc-matrix-${u.id}`}>
      <div className="pmatrix-head">
        <div className="u-panel-label">Permissions</div>
        {isBoss && <span className="pmatrix-note"><Lock aria-hidden="true" /> Boss — accès complet, non restreignable</span>}
        {!isBoss && overridden && (
          <button className="pmatrix-reset" onClick={reset} disabled={!canManage} data-testid={`acc-reset-${u.id}`}>
            <RotateCcw aria-hidden="true" /> Rétablir le rôle
          </button>
        )}
      </div>
      <div className="pmatrix-groups">
        {groups.map(({ group, perms }) => (
          <div key={group} className="pgroup">
            <div className="pgroup-name">{group}</div>
            {perms.map((p) => {
              const meta = PERMISSION_META[p];
              const base = hasPermission(u.role, p);
              const on = effective.has(p);
              const custom = on !== base;
              return (
                <button key={p} type="button"
                  className={'prow' + (on ? ' on' : '') + (custom ? ' custom' : '')}
                  disabled={locked} data-testid={`acc-perm-${u.id}-${p}`}
                  title={meta.desc + (custom ? ' · personnalisé (diffère du rôle)' : '')}
                  onClick={() => toggle(p)}>
                  <span className="prow-state">{on ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}</span>
                  <span className="prow-label">{meta.label}</span>
                  {custom && <span className="prow-dot" aria-label="Personnalisé" />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
